import {
    rotateImageFileFromMediaItem,
    updateCachedMediaLocalFile,
    getRawFileFromMediaItem,
} from './fileUtils'

export class MediaUtility {
    static hasMediaId(mediaId) {
        return (
            mediaId !== undefined &&
            mediaId !== null &&
            String(mediaId) !== ''
        )
    }

    static resolvePreviewUrl(file = {}) {
        if (file?.local && file?.url) {
            const localUrl = String(file.url)
            if (
                localUrl.startsWith('blob:') ||
                localUrl.startsWith('data:')
            ) {
                return localUrl
            }
        }
        const candidates = [
            file.SignedURL,
            file.FileURL,
            file.url,
            file.ImagePathUrl,
        ]
        const match = candidates.find(c => {
            if (!c) return false
            const s = String(c)
            return (
                s.startsWith('http') ||
                s.startsWith('blob:') ||
                s.startsWith('data:')
            )
        })
        return match ? String(match) : ''
    }

    static checkIsImage(file) {
        const url = MediaUtility.resolvePreviewUrl(file) || file?.url
        if (file?.file?.name?.match(/\.(jpeg|jpg|gif|png|svg|webp)$/i)) {
            return true
        }
        if (!url) {
            return !!file?.local
        }
        if (url.startsWith('blob:') || url.startsWith('data:image/')) {
            return true
        }
        return (
            url.match(/\.(jpeg|jpg|gif|png|svg|webp)/i) ||
            /\.(jpeg|jpg|gif|png|svg|webp)(\?.*)?$/i.test(url)
        )
    }

    static checkIsPDF(file) {
        const url = MediaUtility.resolvePreviewUrl(file) || file?.url
        if (file?.file?.name?.match(/\.(pdf)$/i)) {
            return true
        }
        if (!url) {
            return false
        }
        return (
            url.match(/\.(pdf)/i) ||
            /\.(pdf)(\?.*)?$/i.test(url) ||
            file?.file?.name?.match(/\.(pdf)$/i)
        )
    }
}

export const getMediaMergeKey = file => {
    const mediaId = file?.MediaId ?? file?.Unique_Id ?? file?.id
    if (MediaUtility.hasMediaId(mediaId)) {
        return `mid:${mediaId}`
    }
    if (file?.FileKey) {
        return `fk:${file.FileKey}`
    }
    const url = file?.SignedURL || file?.FileURL || file?.url
    if (url) {
        return `url:${url}`
    }
    return ''
}

/** Prefer non-empty Caption/Comment when the same media appears in multiple question arrays. */
export const mergeMediaItemMeta = (prev = {}, next = {}) => {
    const caption = String(next?.Caption ?? '').trim()
        ? next.Caption
        : prev?.Caption ?? ''
    const comment = String(next?.Comment ?? '').trim()
        ? next.Comment
        : prev?.Comment ?? ''
    return { ...prev, ...next, Caption: caption, Comment: comment }
}

/** Keep Caption/Comment on existing files when another upload refreshes the Files array. */
export const mergeQuestionFilesPreservingMeta = (prevFiles = [], nextFiles = []) => {
    const prevMap = new Map();
    (prevFiles || []).forEach(file => {
        const key = getMediaMergeKey(file)
        if (key) {
            prevMap.set(key, file)
        }
    })

    return (nextFiles || []).map(file => {
        const key = getMediaMergeKey(file)
        const prev = key ? prevMap.get(key) : null
        if (!prev) {
            return file
        }
        return mergeMediaItemMeta(prev, file)
    })
}

export const getQuestionMediaList = questionItem => {
    const byKey = new Map()
    const lists = [
        questionItem?.Answers?.MediaResponse,
        questionItem?.MediaAnswers,
        questionItem?.Files,
    ]

    const addList = list => {
        (list || []).forEach(f => {
            const key = getMediaMergeKey(f) || `local:${byKey.size}`
            if (!byKey.has(key)) {
                byKey.set(key, f)
            } else {
                byKey.set(key, mergeMediaItemMeta(byKey.get(key), f))
            }
        })
    }

    lists.forEach(addList)
    return Array.from(byKey.values())
}

/** Keep Files and Answers.MediaResponse in sync for inspection questions. */
export const syncQuestionMediaFields = question => {
    const files = mergeQuestionFilesPreservingMeta(
        getQuestionMediaList(question),
        question?.Files || [],
    )
    const next = { ...question, Files: files }
    if (next.Answers && typeof next.Answers === 'object') {
        next.Answers = { ...next.Answers, MediaResponse: files }
    }
    return next
}

export const mapInspectionQuestionMediaFiles = questionItem => {
    const normalized = getQuestionMediaList(questionItem)
        .map((ele, i) => {
            const previewUrl = MediaUtility.resolvePreviewUrl(ele)
            if (!previewUrl && !ele?.local) {
                return null
            }
            return {
                ...ele,
                id:
                    ele.MediaId ||
                    ele.Unique_Id ||
                    ele.id ||
                    ele.FileKey ||
                    previewUrl ||
                    `local-${i}`,
                name: ele.FileKey || `File ${i + 1}`,
                url: previewUrl || (ele.local ? ele.url : ''),
                FileURL: previewUrl || ele.FileURL,
                SignedURL: ele.SignedURL,
                FileKey: ele.FileKey,
                Caption: ele.Caption || '',
                Comment: ele.Comment || '',
            }
        })
        .filter(Boolean)

    // Collapse duplicates that may come from Files + MediaResponse + MediaAnswers
    // representations of the same media item.
    return normalized.reduce((acc, item) => {
        const existingIndex = acc.findIndex(existing => {
            const existingMediaId = existing?.MediaId ?? existing?.id
            const itemMediaId = item?.MediaId ?? item?.id
            if (
                MediaUtility.hasMediaId(existingMediaId) &&
                MediaUtility.hasMediaId(itemMediaId) &&
                String(existingMediaId) === String(itemMediaId)
            ) {
                return true
            }

            const existingFileKey = String(existing?.FileKey || '').trim()
            const itemFileKey = String(item?.FileKey || '').trim()
            if (existingFileKey && itemFileKey && existingFileKey === itemFileKey) {
                return true
            }

            const existingUrl = String(
                existing?.SignedURL || existing?.FileURL || existing?.url || '',
            ).trim()
            const itemUrl = String(
                item?.SignedURL || item?.FileURL || item?.url || '',
            ).trim()
            return !!existingUrl && !!itemUrl && existingUrl === itemUrl
        })

        if (existingIndex === -1) {
            acc.push(item)
            return acc
        }

        acc[existingIndex] = mergeMediaItemMeta(acc[existingIndex], item)
        return acc
    }, [])
}

/** Merge upload API results into a local files array (used by media rotate flow). */
export const applyUploadedMediaToFileList = (prev, uploaded) => {
    if (!Array.isArray(uploaded) || !uploaded.length) {
        return prev
    }
    const next = [...prev]
    uploaded.forEach(file => {
        const mediaId = file?.MediaId ?? file?.id
        const idx = next.findIndex(
            f =>
                (mediaId != null
                    && String(f?.MediaId ?? f?.id ?? '') === String(mediaId))
                || (file?.FileKey && f?.FileKey === file.FileKey),
        )
        const previewUrl =
            file?.SignedURL
            || file?.FileURL
            || file?.ImagePathUrl
            || file?.url
            || ''
        const merged = {
            ...(idx >= 0 ? next[idx] : {}),
            ...file,
            local: false,
            url: previewUrl || (idx >= 0 ? next[idx]?.url : ''),
            id: mediaId || file?.FileKey || file?.id,
        }
        if (idx >= 0) {
            next[idx] = merged
        } else {
            next.push(merged)
        }
    })
    return next
}

export async function buildRotatedMediaItem(mediaItem, degrees = 90) {
    const rotated = await rotateImageFileFromMediaItem(mediaItem, degrees)
    if (mediaItem?.url?.startsWith('blob:')) {
        try {
            URL.revokeObjectURL(mediaItem.url)
        } catch (e) {
            // ignore revoke errors
        }
    }
    const updatedItem = {
        ...mediaItem,
        local: true,
        url: rotated.url,
        SignedURL: rotated.url,
        FileURL: rotated.url,
        file: {
            name: rotated.file.name,
            file: rotated.file,
        },
    }
    updateCachedMediaLocalFile(mediaItem, rotated.file)
    updateCachedMediaLocalFile(updatedItem, rotated.file)
    return { updatedItem, rotated }
}

const isSameMediaItem = (a, b) => {
    if (
        a?.MediaId != null &&
        b?.MediaId != null &&
        String(a.MediaId) === String(b.MediaId)
    ) {
        return true
    }
    if (
        a?.id != null &&
        b?.id != null &&
        String(a.id) === String(b.id)
    ) {
        return true
    }
    if (a?.FileKey && b?.FileKey && a.FileKey === b.FileKey) {
        return true
    }
    return false
}

export const replaceMediaItemInList = (files, mediaItem, updatedItem) =>
    files.map(f => (isSameMediaItem(f, mediaItem) ? updatedItem : f))

export const mergeLocalMediaState = (serverFiles, prevFiles) =>
    (serverFiles || []).map(serverFile => {
        const prevMatch = (prevFiles || []).find(p =>
            isSameMediaItem(p, serverFile),
        )
        if (!prevMatch) {
            return serverFile
        }
        if (prevMatch.local && String(prevMatch.url || '').startsWith('blob:')) {
            return {
                ...serverFile,
                ...prevMatch,
                MediaId: serverFile.MediaId ?? prevMatch.MediaId,
                id: serverFile.id ?? prevMatch.id,
                url: prevMatch.url,
                SignedURL: prevMatch.url,
                FileURL: prevMatch.url,
            }
        }
        if (getRawFileFromMediaItem(prevMatch)) {
            return {
                ...serverFile,
                file: prevMatch.file,
            }
        }
        return serverFile
    })
